import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import config from '../config';
import { Smartphone } from 'lucide-react';
import { getCosClassName, getCpaClassName } from '../utils/metricsUtils';

/**
 * Composant modulaire pour afficher les performances par appareil des campagnes Performance Max
 */
const PerformanceMaxDevicesTableModular = React.memo(({
  selectedAccount,
  selectedPeriod,
  customStartDate,
  customEndDate,
  formatNumber,
  isVisible = true,
  onToggleVisibility,
  token,
  setError,
  selectedCampaignFilter,
  pmaxLabelsFilter,
  campaignLabelsMap,
  performanceMaxChannelsData,
  isPmaxMetricVisible,
  moduleId = 'pmax_devices'
}) => {

  // États locaux pour les données
  const [devicesData, setDevicesData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: 'cost', direction: 'desc' });

  // Calculer les IDs des campagnes PMAX filtrées
  const getFilteredCampaignIds = () => {
    // Si pas de données de canaux PMAX, retourner null (on récupérera tous les devices)
    if (!performanceMaxChannelsData || performanceMaxChannelsData.length === 0) {
      return null;
    }

    // Filtrer les campagnes selon les filtres actifs
    const filteredIds = performanceMaxChannelsData
      .filter(campaign => {
        const campaignId = (campaign.id || campaign.campaignId || '').toString();

        // Filtre par campagne spécifique
        if (selectedCampaignFilter) {
          const filterId = (selectedCampaignFilter.id || selectedCampaignFilter.campaignId || '').toString();
          const filterName = selectedCampaignFilter.name || selectedCampaignFilter.campaignName || '';
          if (campaignId !== filterId && campaign.name !== filterName) {
            return false;
          }
        }

        // Filtre par labels
        if (pmaxLabelsFilter && pmaxLabelsFilter.length > 0) {
          const labels = (campaignLabelsMap && campaignLabelsMap[campaignId]) || [];
          if (!pmaxLabelsFilter.some(label => labels.includes(label))) {
            return false;
          }
        }

        return true;
      })
      .map(c => c.id || c.campaignId)
      .filter(Boolean);

    return filteredIds.length > 0 ? filteredIds : null;
  };

  // Charger les données quand le composant est monté ou que les dépendances changent
  useEffect(() => {
    if (!selectedAccount || !selectedPeriod || !token) return;

    const loadDevicesData = async () => {
      try {
        setLoading(true);
        const filteredCampaignIds = getFilteredCampaignIds();
        console.log('📱 [PMAX-DEVICES-MODULAR] Chargement des données appareils PMAX...',
          filteredCampaignIds ? `(${filteredCampaignIds.length} campagnes filtrées)` : '(toutes campagnes PMAX)');

        const requestBody = {
          sources: [selectedAccount.id],
          period: selectedPeriod
        };

        // Ajouter les dates personnalisées si période CUSTOM
        if (selectedPeriod === 'CUSTOM') {
          if (!customStartDate || !customEndDate) return;
          requestBody.customStartDate = customStartDate;
          requestBody.customEndDate = customEndDate;
        }

        // Ajouter les campaignIds filtrés (campagnes PMAX uniquement)
        if (filteredCampaignIds) {
          requestBody.campaignIds = filteredCampaignIds;
        }

        console.log('📱 [PMAX-DEVICES-MODULAR] Requête vers:', `${config.API_URL}/api/dashboard/components/performance-max-devices`);

        const response = await axios.post(
          `${config.API_URL}/api/dashboard/components/performance-max-devices`,
          requestBody,
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );

        console.log('📱 [PMAX-DEVICES-MODULAR] Réponse reçue:', response.data);

        if (response.data.success && response.data.rawData) {
          const accountData = response.data.rawData[selectedAccount.id];
          if (accountData && accountData.devices) {
            setDevicesData(accountData);
            setIsDataLoaded(true);
            console.log('✅ [PMAX-DEVICES-MODULAR] Données chargées:', {
              totalDevices: accountData.devices.length,
              period: selectedPeriod
            });
          } else {
            console.log('❌ [PMAX-DEVICES-MODULAR] Pas de données appareils');
            setDevicesData(null);
          }
        } else {
          console.error('❌ [PMAX-DEVICES-MODULAR] API retourne success=false:', response.data);
          setError && setError('Erreur lors du chargement des données appareils');
        }
      } catch (error) {
        console.error('❌ [PMAX-DEVICES-MODULAR] Erreur chargement:', error);
        setError && setError('Erreur lors du chargement des données appareils');
      } finally {
        setLoading(false);
      }
    };

    loadDevicesData();
  }, [selectedAccount, selectedPeriod, customStartDate, customEndDate, token, selectedCampaignFilter, pmaxLabelsFilter, performanceMaxChannelsData?.length]);

  // Formater les données des appareils avec calcul des métriques dérivées
  const formattedDevices = useMemo(() => {
    if (!devicesData?.devices) return [];

    return devicesData.devices.map(device => {
      const impressions = device.impressions || 0;
      const clicks = device.clicks || 0;
      const cost = device.cost || 0;
      const conversions = device.conversions || 0;
      const conversionsValue = device.conversionsValue || 0;

      return {
        ...device,
        name: getDeviceDisplayName(device.device || device.name),
        impressions,
        clicks,
        cost,
        conversions,
        conversionsValue,
        ctr: impressions > 0 ? (clicks / impressions * 100) : 0,
        cpc: clicks > 0 ? (cost / clicks) : 0,
        conversionRate: clicks > 0 ? (conversions / clicks * 100) : 0,
        cpa: conversions > 0 ? (cost / conversions) : 0,
        cos: conversionsValue > 0 ? (cost / conversionsValue * 100) : 0
      };
    });
  }, [devicesData]);

  // Fonction pour obtenir le nom d'affichage d'un appareil
  function getDeviceDisplayName(deviceType) {
    const deviceNames = {
      'MOBILE': '📱 Mobile',
      'DESKTOP': '💻 Ordinateur',
      'TABLET': '📲 Tablette',
      'CONNECTED_TV': '📺 TV connectée',
      'OTHER': '🔌 Autre',
      'UNKNOWN': '❓ Inconnu'
    };
    return deviceNames[deviceType] || deviceType || '❓ Inconnu';
  }

  // Tri des appareils
  const sortedDevices = useMemo(() => {
    return [...formattedDevices].sort((a, b) => {
      let aValue, bValue;

      switch(sortConfig.key) {
        case 'name':
          aValue = a.name || '';
          bValue = b.name || '';
          return sortConfig.direction === 'asc'
            ? aValue.localeCompare(bValue)
            : bValue.localeCompare(aValue);
        default:
          aValue = a[sortConfig.key] || 0;
          bValue = b[sortConfig.key] || 0;
          if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
          if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
          return 0;
      }
    });
  }, [formattedDevices, sortConfig]);

  // Fonction de gestion du tri
  const handleSort = (key) => {
    let direction = 'desc';
    if (sortConfig.key === key && sortConfig.direction === 'desc') {
      direction = 'asc';
    }
    setSortConfig({ key, direction });
  };

  // Calculer les totaux
  const totals = useMemo(() => {
    if (!formattedDevices.length) return null;

    const sums = formattedDevices.reduce((acc, device) => ({
      impressions: acc.impressions + device.impressions,
      clicks: acc.clicks + device.clicks,
      cost: acc.cost + device.cost,
      conversions: acc.conversions + device.conversions,
      conversionsValue: acc.conversionsValue + device.conversionsValue
    }), {
      impressions: 0,
      clicks: 0,
      cost: 0,
      conversions: 0,
      conversionsValue: 0
    });

    return {
      ...sums,
      ctr: sums.impressions > 0 ? (sums.clicks / sums.impressions * 100) : 0,
      cpc: sums.clicks > 0 ? (sums.cost / sums.clicks) : 0,
      conversionRate: sums.clicks > 0 ? (sums.conversions / sums.clicks * 100) : 0,
      cpa: sums.conversions > 0 ? (sums.cost / sums.conversions) : 0,
      cos: sums.conversionsValue > 0 ? (sums.cost / sums.conversionsValue * 100) : 0
    };
  }, [formattedDevices]);

  // Ne pas afficher si pas de données et pas en chargement
  if (!loading && !isDataLoaded) return null;

  // Ne pas afficher si aucune donnée
  if (!loading && (!sortedDevices || sortedDevices.length === 0)) return null;

  return (
    <div className="campaigns-table-section">
      <div className="section-header">
        <h4><Smartphone size={16} />&nbsp;Performance Max - Appareils</h4>
        <button
          onClick={onToggleVisibility}
          className="section-toggle-btn"
          title={isVisible ? "Masquer la section" : "Afficher la section"}
        >
          {isVisible ? '−' : '+'}
        </button>
      </div>

      {isVisible && (
        <>
          {loading ? (
            <div className="loading-message" style={{ textAlign: 'center', padding: '40px' }}>
              <div className="loading-spinner" style={{ margin: '0 auto' }}></div>
              Chargement des données par appareil...<br /><br />
            </div>
          ) : sortedDevices && sortedDevices.length > 0 ? (
            <div className="campaigns-table-wrapper">
              <table className="campaigns-table" style={{ tableLayout: "auto" }}>
                <thead>
                  <tr>
                    <th onClick={() => handleSort('name')} className="sortable">
                      Appareil {sortConfig.key === 'name' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                    </th>
                    {isPmaxMetricVisible('impressions') && (
                      <th onClick={() => handleSort('impressions')} className="sortable numeric">
                        Impr. {sortConfig.key === 'impressions' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                      </th>
                    )}
                    {isPmaxMetricVisible('ctr') && (
                      <th onClick={() => handleSort('ctr')} className="sortable numeric">
                        CTR {sortConfig.key === 'ctr' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                      </th>
                    )}
                    {isPmaxMetricVisible('clics') && (
                      <th onClick={() => handleSort('clicks')} className="sortable numeric">
                        Clics {sortConfig.key === 'clicks' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                      </th>
                    )}
                    {isPmaxMetricVisible('cpc') && (
                      <th onClick={() => handleSort('cpc')} className="sortable numeric">
                        CPC {sortConfig.key === 'cpc' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                      </th>
                    )}
                    {isPmaxMetricVisible('cout') && (
                      <th onClick={() => handleSort('cost')} className="sortable numeric">
                        Coût {sortConfig.key === 'cost' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                      </th>
                    )}
                    {isPmaxMetricVisible('taux_conv') && (
                      <th onClick={() => handleSort('conversionRate')} className="sortable numeric">
                        Taux Conv. {sortConfig.key === 'conversionRate' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                      </th>
                    )}
                    {isPmaxMetricVisible('conv') && (
                      <th onClick={() => handleSort('conversions')} className="sortable numeric">
                        Conv. {sortConfig.key === 'conversions' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                      </th>
                    )}
                    {isPmaxMetricVisible('cpa') && (
                      <th onClick={() => handleSort('cpa')} className="sortable numeric">
                        CPA {sortConfig.key === 'cpa' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                      </th>
                    )}
                    {isPmaxMetricVisible('valeur_conv') && (
                      <th onClick={() => handleSort('conversionsValue')} className="sortable numeric">
                        Valeur Conv. {sortConfig.key === 'conversionsValue' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                      </th>
                    )}
                    {isPmaxMetricVisible('cos') && (
                      <th onClick={() => handleSort('cos')} className="sortable numeric">
                        COS {sortConfig.key === 'cos' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {sortedDevices.map((device, index) => (
                    <tr key={`${device.device || device.name}-${index}`}>
                      <td style={{ fontWeight: '500' }}>
                        {device.name}
                      </td>
                      {isPmaxMetricVisible('impressions') && <td className="numeric">{formatNumber(device.impressions)}</td>}
                      {isPmaxMetricVisible('ctr') && <td className="numeric">{formatNumber(device.ctr, 2)}%</td>}
                      {isPmaxMetricVisible('clics') && <td className="numeric">{formatNumber(device.clicks)}</td>}
                      {isPmaxMetricVisible('cpc') && <td className="numeric">{formatNumber(device.cpc, 2)}€</td>}
                      {isPmaxMetricVisible('cout') && <td className="numeric">{formatNumber(device.cost, 2)}€</td>}
                      {isPmaxMetricVisible('taux_conv') && <td className="numeric">{formatNumber(device.conversionRate, 2)}%</td>}
                      {isPmaxMetricVisible('conv') && <td className="numeric">{formatNumber(device.conversions, 1)}</td>}
                      {isPmaxMetricVisible('cpa') && (
                        <td className={`numeric ${getCpaClassName(device.cpa || 0, selectedAccount?.id || 'global', moduleId)}`}>
                          {device.conversions > 0 ? `${formatNumber(device.cpa, 2)}€` : '∞'}
                        </td>
                      )}
                      {isPmaxMetricVisible('valeur_conv') && <td className="numeric">{formatNumber(device.conversionsValue, 2)}€</td>}
                      {isPmaxMetricVisible('cos') && (
                        <td className={`numeric ${getCosClassName(device.cos, selectedAccount?.id || 'global', moduleId)}`}>
                          {formatNumber(device.cos, 1)}%
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
                {totals && (
                  <tfoot>
                    <tr className="total-row">
                      <td><strong>Total ({sortedDevices.length} appareils)</strong></td>
                      {isPmaxMetricVisible('impressions') && <td className="numeric"><strong>{formatNumber(totals.impressions)}</strong></td>}
                      {isPmaxMetricVisible('ctr') && (
                        <td className="numeric"><strong>{formatNumber(totals.ctr, 2)}%</strong></td>
                      )}
                      {isPmaxMetricVisible('clics') && <td className="numeric"><strong>{formatNumber(totals.clicks)}</strong></td>}
                      {isPmaxMetricVisible('cpc') && (
                        <td className="numeric"><strong>{formatNumber(totals.cpc, 2)}€</strong></td>
                      )}
                      {isPmaxMetricVisible('cout') && <td className="numeric"><strong>{formatNumber(totals.cost, 2)}€</strong></td>}
                      {isPmaxMetricVisible('taux_conv') && (
                        <td className="numeric"><strong>{formatNumber(totals.conversionRate, 2)}%</strong></td>
                      )}
                      {isPmaxMetricVisible('conv') && <td className="numeric"><strong>{formatNumber(totals.conversions, 1)}</strong></td>}
                      {isPmaxMetricVisible('cpa') && (
                        <td className={`numeric ${getCpaClassName(totals.cpa, selectedAccount?.id || 'global', moduleId)}`}>
                          <strong>{totals.conversions > 0 ? formatNumber(totals.cpa, 2) + '€' : '∞'}</strong>
                        </td>
                      )}
                      {isPmaxMetricVisible('valeur_conv') && <td className="numeric"><strong>{formatNumber(totals.conversionsValue, 2)}€</strong></td>}
                      {isPmaxMetricVisible('cos') && (
                        <td className={`numeric ${getCosClassName(totals.cos, selectedAccount?.id || 'global', moduleId)}`}>
                          <strong>{totals.conversionsValue > 0 ? formatNumber(totals.cos, 1) + '%' : '∞'}</strong>
                        </td>
                      )}
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          ) : (
            <div style={{ textAlign: 'center', color: '#666', padding: '40px' }}>
              Aucune donnée par appareil pour la période sélectionnée
            </div>
          )}
        </>
      )}
    </div>
  );
});

PerformanceMaxDevicesTableModular.displayName = 'PerformanceMaxDevicesTableModular';

export default PerformanceMaxDevicesTableModular;
